// frontend/src/components/Admin/AdminDashboard.jsx
//
// Admin-only view at #/admin: user list, query log, and usage stats from
// /api/admin/overview. Access is enforced by the backend (ADMIN_EMAILS) —
// non-admins get a 403 and see the "not authorized" state here.
import React, { useCallback, useEffect, useState } from 'react';
import { Users, Activity, BarChart3, RefreshCw, ArrowLeft, ShieldAlert } from 'lucide-react';
import { fetchAdminOverview } from '../../services/api';

const fmtTime = (sec) => (sec ? new Date(sec * 1000).toLocaleString() : '—');

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="flex-1 min-w-[140px] bg-white rounded-lg border border-gray-200 p-4">
    <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
      <Icon size={14} /> {label}
    </div>
    <div className="text-2xl font-bold text-gray-900">{value}</div>
  </div>
);

const AdminDashboard = ({ onBack }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchAdminOverview()
      .then(setData)
      .catch((e) => setError(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="max-w-lg mx-auto mt-16 bg-white rounded-lg border border-gray-200 p-8 text-center">
        <ShieldAlert className="mx-auto text-amber-500 mb-3" size={32} />
        <div className="text-gray-900 font-semibold mb-1">Not authorized</div>
        <div className="text-sm text-gray-500 mb-4">
          This dashboard is restricted to admin accounts. ({error})
        </div>
        <button onClick={onBack} className="text-sm text-emerald-700 hover:underline">← Back to the app</button>
      </div>
    );
  }

  const stats = data?.stats || {};
  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} title="Back"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
            <ArrowLeft size={16} />
          </button>
          <h2 className="text-xl font-bold text-gray-900">Admin dashboard</h2>
        </div>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-3">
        <StatCard icon={Users} label="Users" value={stats.totalUsers ?? '—'} />
        <StatCard icon={Activity} label="Queries (24h)" value={stats.queries24h ?? '—'} />
        <StatCard icon={Activity} label="Queries (all time)" value={stats.totalQueries ?? '—'} />
        <StatCard icon={Users} label="Active users (7d)" value={stats.activeUsers7d ?? '—'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Users */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-4 overflow-x-auto">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Users</h3>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-400">
                <th className="py-1 pr-3 font-normal">User</th>
                <th className="py-1 pr-3 font-normal">Email</th>
                <th className="py-1 pr-3 font-normal text-right">Logins</th>
                <th className="py-1 font-normal text-right">Last login</th>
              </tr>
            </thead>
            <tbody>
              {(data?.users || []).map((u) => (
                <tr key={u.email} className="border-t border-gray-100">
                  <td className="py-1.5 pr-3">
                    <span className="flex items-center gap-2">
                      {u.picture && <img src={u.picture} alt="" className="h-5 w-5 rounded-full" />}
                      <span className="font-medium text-gray-800">{u.name || '—'}</span>
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-gray-600">{u.email}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{u.login_count}</td>
                  <td className="py-1.5 text-right text-gray-500">{fmtTime(u.last_login_at)}</td>
                </tr>
              ))}
              {!loading && (data?.users || []).length === 0 && (
                <tr><td colSpan={4} className="py-4 text-center text-gray-400">No users yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Top symbols */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <BarChart3 size={14} /> Top symbols
          </h3>
          <div className="space-y-1.5">
            {(stats.topSymbols || []).map((s) => (
              <div key={s.symbol} className="flex items-center justify-between text-xs">
                <span className="font-medium text-gray-700">{s.symbol}</span>
                <span className="text-gray-400 font-mono">{s.count}</span>
              </div>
            ))}
            {!loading && (stats.topSymbols || []).length === 0 && (
              <div className="text-xs text-gray-400">No queries yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Query log */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 overflow-x-auto">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Recent queries</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="py-1 pr-3 font-normal">Time</th>
              <th className="py-1 pr-3 font-normal">User</th>
              <th className="py-1 pr-3 font-normal">Endpoint</th>
              <th className="py-1 pr-3 font-normal">Symbol</th>
              <th className="py-1 font-normal">Timeframe</th>
            </tr>
          </thead>
          <tbody>
            {(data?.recentQueries || []).map((q, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="py-1 pr-3 text-gray-500 whitespace-nowrap">{fmtTime(q.created_at)}</td>
                <td className="py-1 pr-3 text-gray-600">{q.user_email || <span className="text-gray-300">anonymous</span>}</td>
                <td className="py-1 pr-3">{q.endpoint}</td>
                <td className="py-1 pr-3 font-medium text-gray-800">{q.symbol || '—'}</td>
                <td className="py-1 text-gray-500">{q.timeframe || '—'}</td>
              </tr>
            ))}
            {!loading && (data?.recentQueries || []).length === 0 && (
              <tr><td colSpan={5} className="py-4 text-center text-gray-400">No queries yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDashboard;
